import { useCallback, useEffect, useRef, useState } from "react";
import type { ProductionWorkspaceReport } from "../../../shared/production/workspace-report";
import type { SavedProductionPlanSummary } from "../../../shared/production/production-plan-summary";
import { apiRequest } from "../../shared/api/client";
import { todayInIstanbul } from "./production-report-format";
import { addDays } from "./production-workspace-model";
import { endpoint, query, sameSelection } from "./production-report-selection";
import type { Selection } from "./production-report-selection";

export function useProductionReportDataWorkspace(dataRefreshVersion: number) {
  const [today, setToday] = useState(todayInIstanbul);
  const [plans, setPlans] = useState<SavedProductionPlanSummary[]>([]);
  const [draft, setDraft] = useState<Selection>(() => ({ planId: "", from: todayInIstanbul(), to: todayInIstanbul() }));
  const [applied, setApplied] = useState<Selection | null>(null);
  const [report, setReport] = useState<ProductionWorkspaceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false);
  const [retry, setRetry] = useState(0);
  const version = useRef(0), selectionRef = useRef<Selection | null>(null), refreshInFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; version.current++; }; }, []);
  const load = useCallback(async (selection: Selection, background = false) => {
    if (!mounted.current || (background && refreshInFlight.current)) return;
    const current = ++version.current;
    if (background) { refreshInFlight.current = true; setRefreshing(true); }
    else { setLoading(true); setError(""); }
    try {
      const result = await apiRequest<ProductionWorkspaceReport>(`${endpoint}?${query(selection)}`, { cache: "no-store" });
      if (current !== version.current) return;
      setReport(result); setApplied(selection); selectionRef.current = selection; setStale(false); setError("");
    } catch (caught) {
      if (current !== version.current) return;
      if (background) setStale(true);
      else { setReport(null); setError(caught instanceof Error ? caught.message : "Üretim verisi alınamadı."); }
    } finally {
      if (current === version.current) { setLoading(false); setRefreshing(false); }
      if (background) refreshInFlight.current = false;
    }
  }, []);
  useEffect(() => {
    let active = true;
    const current = ++version.current;
    setLoading(true); setError("");
    void (async () => {
      try {
        const { items } = await apiRequest<{ items: SavedProductionPlanSummary[] }>("/api/production/reports/plans", { cache: "no-store" });
        if (!active || current !== version.current) return;
        setPlans(items);
        const now = todayInIstanbul(), previous = selectionRef.current;
        const chosen = items.find(plan => plan.id === previous?.planId) ?? items.find(plan => plan.status === "ACTIVE" && plan.weekStart <= now && plan.weekEnd >= now) ?? items.find(plan => plan.status === "ACTIVE");
        const selection = previous ?? { planId: chosen?.id ?? "", from: now, to: now };
        setDraft(selection); await load(selection);
      } catch (caught) {
        if (active && current === version.current) { setLoading(false); setError(caught instanceof Error ? caught.message : "Plan listesi okunamadı."); }
      }
    })();
    return () => { active = false; version.current++; };
  }, [dataRefreshVersion, retry, load]);
  const pending = Boolean(applied && !sameSelection(draft, applied));
  const isLive = Boolean(report && report.range.from <= today && report.range.to >= today);
  useEffect(() => {
    const tick = () => {
      if (!mounted.current) return;
      setToday(todayInIstanbul());
      if (isLive && !pending && !loading && applied && document.visibilityState === "visible") void load(applied, true);
    };
    const timer = window.setInterval(tick, 60_000);
    const visibility = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", visibility);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", visibility); };
  }, [isLive, pending, loading, applied, load]);
  const invalid = !draft.from || !draft.to || draft.from > draft.to || draft.to > today || (Date.parse(draft.to) - Date.parse(draft.from)) / 86_400_000 >= 31;
  const selectedPlan = plans.find(plan => plan.id === draft.planId);
  const quickDate = (kind: "today" | "yesterday" | "week") => {
    const from = kind === "today" ? today : kind === "yesterday" ? addDays(today, -1) : selectedPlan?.weekStart ?? addDays(today, -6);
    const to = kind === "week" ? selectedPlan && selectedPlan.weekEnd < today ? selectedPlan.weekEnd : today : from;
    setDraft(previous => ({ ...previous, from, to }));
  };
  return { today, plans, draft, setDraft, applied, report, loading, refreshing, error, stale, setRetry, load, pending, isLive, invalid, selectedPlan, quickDate };
}
